import type { Companies } from "@/src/gql/graphql";

import {
  addCustomerName,
  addCustomerRef,
  findCustomerName,
  getInvoiceCustomerDisplayNameGeneric,
  parseCustomerName,
  parseCustomerRef,
  removeCustomerName,
  removeCustomerRef,
  serializeCustomerName,
  serializeCustomerRef,
} from "./customer-ref-base";

export type {
  QuickBooksCustomerRef,
  QuickBooksCustomerName,
  InvoiceDisplayItem,
} from "./customer-ref-base";
import type {
  QuickBooksCustomerRef,
  QuickBooksCustomerName,
  InvoiceDisplayItem,
} from "./customer-ref-base";

/**
 * Parse the quickbooks_customer_ref field which stores multiple customer references
 * in the format: "customer1;realmId,customer2;realmId"
 */
export function parseQuickBooksCustomerRef(
  customerRef: string | null | undefined
): QuickBooksCustomerRef[] {
  return parseCustomerRef(customerRef);
}

/**
 * Serialize QuickBooks customer references back to the quickbooks_customer_ref format
 */
export function serializeQuickBooksCustomerRef(refs: QuickBooksCustomerRef[]): string {
  return serializeCustomerRef(refs);
}

/**
 * Add a new QuickBooks customer reference to an existing company ref string
 */
export function addQuickBooksCustomerRef(
  existingRef: string | null | undefined,
  customerId: string,
  realmId?: string
): string {
  return addCustomerRef(existingRef, customerId, realmId);
}

/**
 * Remove a QuickBooks customer reference from an existing company ref string
 */
export function removeQuickBooksCustomerRef(
  existingRef: string | null | undefined,
  customerId: string
): string {
  return removeCustomerRef(existingRef, customerId);
}

/**
 * Check if a company has a specific QuickBooks customer reference
 */
export function companyHasQuickBooksCustomerRef(
  company: Companies | null | undefined,
  customerId: string
): boolean {
  if (!company) return false;
  const refs = parseCustomerRef(company.quickbooks_customer_ref);
  return refs.some((ref) => ref.customerId === customerId);
}

/**
 * Get all QuickBooks customer IDs for a company
 */
export function getAllQuickBooksCustomerIds(company: Companies | null | undefined): string[] {
  if (!company) return [];
  const refs = parseCustomerRef(company.quickbooks_customer_ref);
  return refs.map((ref) => ref.customerId);
}

/**
 * Get the primary (first) QuickBooks customer ID for a company
 */
export function getPrimaryQuickBooksCustomerRef(
  company: Companies | null | undefined
): string | undefined {
  if (!company) return undefined;
  const refs = parseCustomerRef(company.quickbooks_customer_ref);
  return refs.length > 0 ? refs[0].customerId : undefined;
}

/**
 * Get all QuickBooks customer references for a company
 */
export function getCompanyQuickBooksCustomerRefs(
  company: Companies | null | undefined
): QuickBooksCustomerRef[] {
  if (!company) return [];
  return parseCustomerRef(company.quickbooks_customer_ref);
}

// ============================================================================
// QUICKBOOKS CUSTOMER NAME MANAGEMENT
// ============================================================================

/**
 * Parse the quickbooks_customer_name field which stores multiple customer names
 * in the format: "customerId:customerName;realmId,customerId2:customerName2;realmId"
 */
export function parseQuickBooksCustomerName(
  customerName: string | null | undefined
): QuickBooksCustomerName[] {
  return parseCustomerName(customerName);
}

/**
 * Serialize QuickBooks customer names back to the quickbooks_customer_name format
 */
export function serializeQuickBooksCustomerName(names: QuickBooksCustomerName[]): string {
  return serializeCustomerName(names);
}

/**
 * Add a new QuickBooks customer name to an existing company name string
 */
export function addQuickBooksCustomerName(
  existingName: string | null | undefined,
  customerId: string,
  customerName: string,
  realmId?: string
): string {
  return addCustomerName(existingName, customerId, customerName, realmId);
}

/**
 * Remove a QuickBooks customer name from an existing company name string
 */
export function removeQuickBooksCustomerName(
  existingName: string | null | undefined,
  customerId: string
): string {
  return removeCustomerName(existingName, customerId);
}

/**
 * Get the QuickBooks customer name for a specific customer ID
 */
export function getQuickBooksCustomerName(
  company: Companies | null | undefined,
  customerId: string
): string | undefined {
  if (!company) return undefined;
  const names = parseCustomerName(company.quickbooks_customer_name);
  return findCustomerName(names, customerId);
}

/**
 * Get all QuickBooks customer names for a company
 */
export function getAllQuickBooksCustomerNames(
  company: Companies | null | undefined
): QuickBooksCustomerName[] {
  if (!company) return [];
  return parseCustomerName(company.quickbooks_customer_name);
}

/**
 * Get the display customer name for an invoice using the quickbooks_customer_name field
 * This is a simplified approach that parses the company name string directly
 */
export function getInvoiceCustomerDisplayName(
  invoiceItem: InvoiceDisplayItem | null | undefined,
  company: Companies | null | undefined
): string {
  return getInvoiceCustomerDisplayNameGeneric(invoiceItem, company?.quickbooks_customer_name);
}
